import { Checkbox } from '@heroui/react';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { BillableServiceView } from '@lims/shared-types';
import { clientsApi, invoicesApi } from '../../api/endpoints.js';
import { useAuth } from '../../app/auth-context.js';
import { PERMISSIONS } from '@lims/permissions';
import {
  ActionBar,
  ActionButton,
  Alert,
  DataTable,
  FilterBar,
  PageHeader,
  Panel,
  SearchField,
  SelectField,
  StatusBadge,
  type DataColumn,
} from '../../components/ui/index.js';
import { displayName } from '../clients/bilingual.js';
import { RecordNumber } from '../clients/record-layout.js';
import { currentLocale } from '../../i18n/index.js';

/**
 * Billing: the work-order service lines a laboratory may still invoice.
 *
 * A LINE MAY BE BILLED MORE THAN ONCE ONLY UP TO ITS QUANTITY. The backend is
 * the one true guard against double billing (D150) — this page shows what it
 * reports (`remainingQuantity`, and every draft or issued invoice already
 * holding the line) so a desk clerk sees the same fact the server will act on.
 *
 * SELECTION IS SCOPED TO ONE CLIENT. An invoice belongs to one client, so the
 * checkboxes stay off until a client is chosen in the filter, rather than
 * letting someone build a mixed selection the next page would have to refuse.
 */
export function BillingPage() {
  const { t } = useTranslation();
  const { can } = useAuth();
  const navigate = useNavigate();
  const locale = currentLocale();

  const [clientId, setClientId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const clients = useQuery({
    queryKey: ['clients', 'selectable'],
    queryFn: () => clientsApi.list({ pageSize: 100, includeSuspended: false }),
  });

  const services = useQuery({
    queryKey: ['billable-services', { clientId, search, page }],
    queryFn: () =>
      invoicesApi.billableServices({
        page,
        search,
        ...(clientId ? { clientId } : {}),
      }),
  });

  const toggle = (service: BillableServiceView) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(service.id)) next.delete(service.id);
      else next.add(service.id);
      return next;
    });
  };

  const columns: readonly DataColumn<BillableServiceView>[] = [
    {
      key: 'select',
      header: t('billing.select'),
      isHeaderHidden: true,
      render: (service) => (
        <Checkbox
          isSelected={selected.has(service.id)}
          isDisabled={!clientId}
          onValueChange={() => toggle(service)}
          aria-label={t('billing.selectLine', {
            description:
              displayName(service.description, locale) ?? service.itemCode ?? '',
          })}
        />
      ),
    },
    {
      key: 'workOrder',
      header: t('billing.workOrder'),
      render: (service) => (
        <RecordNumber to={`/work-orders/${service.workOrder.id}`}>
          {service.workOrder.number}
        </RecordNumber>
      ),
    },
    {
      key: 'client',
      header: t('billing.client'),
      render: (service) => (
        <span className="text-default-800" dir="auto">
          {service.client.name ?? service.client.number}
        </span>
      ),
    },
    {
      key: 'description',
      header: t('billing.description'),
      render: (service) => (
        <div className="min-w-0">
          <span className="block truncate text-default-900">
            {displayName(service.description, locale) ?? t('common.notRecorded')}
          </span>
          {service.itemCode ? (
            <span className="measure text-caption text-default-600" dir="ltr">
              {service.itemCode}
            </span>
          ) : null}
        </div>
      ),
    },
    {
      key: 'state',
      header: t('common.status'),
      render: (service) => (
        <StatusBadge
          label={t(`workOrderServiceState.${service.state}`)}
          tone={service.state === 'complete' ? 'success' : 'neutral'}
        />
      ),
    },
    {
      key: 'remaining',
      header: t('billing.remainingQuantity'),
      align: 'end',
      render: (service) => (
        <div className="text-end">
          <span className="measure block text-default-900" dir="ltr">
            {service.remainingQuantity}
          </span>
          <span className="text-caption text-default-600" dir="ltr">
            {t('billing.ofQuantity', { quantity: service.quantity })}
          </span>
        </div>
      ),
    },
  ];

  const selectedCount = selected.size;

  return (
    <>
      <PageHeader
        title={t('billing.title')}
        subtitle={t('billing.subtitle')}
        breadcrumbs={[{ label: t('nav.billing') }]}
      />

      <div className="mb-stack">
        <Alert tone="info" title={t('billing.chooseClientHint')} />
      </div>

      <Panel>
        <FilterBar>
          <SelectField
            label={t('billing.client')}
            placeholder={t('common.all')}
            value={clientId}
            onChange={(value) => {
              setClientId(value);
              setSelected(new Set());
              setPage(1);
            }}
            options={(clients.data?.data ?? []).map((client) => ({
              value: client.id,
              label: client.name ?? client.clientNumber,
              description: client.clientNumber,
            }))}
          />
          <SearchField
            value={search}
            onChange={(value) => {
              setSearch(value);
              setPage(1);
            }}
            placeholder={t('billing.searchPlaceholder')}
          />
        </FilterBar>

        <DataTable
          label={t('billing.title')}
          columns={columns}
          rows={services.data?.data ?? []}
          rowKey={(service) => service.id}
          isLoading={services.isLoading}
          error={services.error}
          onRetry={() => void services.refetch()}
          emptyContent={t('billing.noneFound')}
          page={page}
          totalPages={services.data?.meta.totalPages ?? 1}
          onPageChange={setPage}
        />
      </Panel>

      {selectedCount > 0 && clientId && can(PERMISSIONS.INVOICES_CREATE) ? (
        // The selection bar (D278): held at the bottom of the view while the
        // list scrolls, so the act the selection is for stays in reach.
        <div className="sticky bottom-4 z-10 mt-gutter flex justify-center">
          <div className="flex max-w-full flex-wrap items-center gap-inline rounded-large border border-default-200 bg-white px-4 py-2 shadow-medium">
            <ActionBar>
              <span className="measure self-center text-body font-semibold text-default-900">
                {t('billing.selectedCount', { count: selectedCount })}
              </span>
              <ActionButton
                priority="primary"
                onPress={() =>
                  navigate(
                    `/invoices/new?clientId=${clientId}&serviceIds=${Array.from(
                      selected,
                    ).join(',')}`,
                  )
                }
              >
                {t('billing.createInvoice')}
              </ActionButton>
            </ActionBar>
          </div>
        </div>
      ) : null}
    </>
  );
}
